import { Button, Col, Form, Row, Spin, notification } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import {
  createThirdPartyBkAccount,
  getThirdPartyBkAccountListByTpid,
  updateThirdPartyBkAccount,
} from 'services/thirdParty';

import { AuthContext } from 'components/Auth/Auth';
import BankAccountModal from './BankAccountModal';
import BankCard from './BankCard';
import ButtonOperation from 'components/ButtonOperation';
import { FormInstance } from 'antd/lib/form';
import GetLabel from 'DisplayFramework/components/GetLabel';
import HeroTitle from 'components/theme_components/HeroTitle';
import ModalSFS from 'components/theme_components/ModalSFS';
import ParListSelect from 'components/ParListSelect';
import { PlusOutlined } from '@ant-design/icons';
import ProductInput from 'pages/ProposalV3/PropDetail/ProductInput';
import TaskSButtonSFS from 'pages/workflow/workflowSFS/TasksButtonSFS';
import Title from 'components/Title';
import moment from 'moment';
import { toDatePickerDate } from 'utils/utils2';
import { useQuery } from 'react-query';
import { Select } from 'antd';
import { useGetProductList } from 'pages/ProposalV3/PropDetail/ProductInput';
import { useMemo } from 'react';

export type BankAccountListProps = {
  form: FormInstance;
  tpid: number;
  color: string;
  itemsCompanyHierarchyTree: any[];
  tptype: string;
  onAccountsCountChange?: (count: number) => void; 
};

const BankAccountList = ({
  form,
  tpid,
  color,
  itemsCompanyHierarchyTree,
  tptype,
   onAccountsCountChange,
}: BankAccountListProps) => {
  const [visible, setVisible] = React.useState(false);
  const { data: products = [], isFetching: loadingProducts } =
    useGetProductList();

  const productOptionsWithEmpty = useMemo(
    () => [
      { label: 'Tous les produits', value: '' },
      ...products.map((p) => ({
        label: p.prlabel,
        value: p.prcode,
      })),
    ],
    [products]
  );

  const [bankAccountsCount, setBankAccountsCount] = useState(0);
  const hasOnlyOneBankAccount = bankAccountsCount <= 1;
  
  const prcodeValue = Form.useWatch('tppaymodein', form);
  const prcode =
    typeof prcodeValue === 'string' ? prcodeValue : prcodeValue?.prcode;

  const {
    data: accounts = [],
    isFetching,
    refetch,
  } = useQuery(
    ['getThirdPartyBkAccountListAction', tpid, prcode],
    async () => {
      const response = await getThirdPartyBkAccountListByTpid({ tpid, prcode });
      return response.list || [];
    },
    {
      enabled: !!tpid, // ensures query only runs if tpid is set
    }
  );

  useEffect(() => {
    if (accounts?.length > 0) {
      const currentValues = form.getFieldValue('bankAccountsComponents') || [];

      const formattedAccounts = accounts.map((bacc) => ({
        bkstartdate: toDatePickerDate(bacc.bkstartdate),
        tpbkid: bacc.tpbkid,
        bkaccountkey: bacc.bkaccountkey,
        bkaccountnum: bacc.bkaccountnum,
        bkbranchcode: bacc.bkbranchcode,
        bkcode: bacc.bkcode,
      }));

      if (JSON.stringify(currentValues) !== JSON.stringify(formattedAccounts)) {
        form.setFieldsValue({
          bankAccountsComponents: formattedAccounts,
        });
      }
    }
    
    // Update bank accounts count
    setBankAccountsCount(accounts.length);
    
    // Call the optional callback to notify parent component
    if (onAccountsCountChange) {
      onAccountsCountChange(accounts.length);
    }
  }, [accounts, onAccountsCountChange]);



  return (
    <Form form={form}>
      <HeroTitle
        form={form}
        color={color}
        display
        title={`bankaccount${tptype}`}
        itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
      />

      <div style={{ marginTop: 24 }}>
        <Row gutter={[16, 16]} justify="center" className="ml-0 mr-0">
          <Col span={4}>
            <Form.Item name="bankaccountprcode" label="Véhicules Finançables">
              <Select
                allowClear
                loading={loadingProducts}
                placeholder="Sélectionnez un produit"
                options={productOptionsWithEmpty}
                className="no-border-radius"
              />
            </Form.Item>
          </Col>
          <Col span={16} />
        </Row>

        <Spin spinning={isFetching}>
          {accounts.length > 0 ? (
            <Row gutter={[16, 16]} justify="center" className="ml-0 mr-0">
              <Col span={22}>
                {accounts.map((account) => (
                  <BankCard
                    key={account.tpbkid}
                    record={account}
                    action="edit"
                    onDelete={refetch}
                    onSave={refetch} 
                    refetch={refetch}
                    onDone={() => {}}
                    deleteDisabled={hasOnlyOneBankAccount}
                  />
                ))}
              </Col>
            </Row>
          ) : (
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              Aucun compte bancaire trouvé pour ce produit.
            </div>
          )}
        </Spin>

        <Row
          gutter={[16, 16]}
          justify="center"
          className="ml-0 mr-0 pb-20 mt-20"
          style={{ marginTop: 30 }}
        >
          <Col>
            <TaskSButtonSFS
              clientName=""
              entityId={tpid}
              entityReference=""
              entityName="THIRDPARTY"
              tptype={tptype}
              title=""
              currcode=""
              funcode="evChangeBankAccount"
              refetch={refetch}
            >
              <ButtonOperation
                comcode="addbankaccount"
                className="ut-btn-bordred"
                icon={
                  <PlusOutlined className="color-orange font-size-20 cursorpointer mr-5 " />
                }
              />
            </TaskSButtonSFS>
          </Col>
        </Row>
      </div>
    </Form>
  );
};

export default BankAccountList;